// Substituição do material de um certificado do cofre.
//
// É a operação central: um upload aqui troca o certificado em TODAS as
// configurações vinculadas de uma vez. Por isso o diálogo insiste no alcance
// antes de deixar confirmar — o operador precisa saber que não está mexendo
// numa configuração só.
import { zodResolver } from "@hookform/resolvers/zod"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import {
  encodePemToBase64,
  PEM_CERTIFICATE_HEADER,
  PEM_PRIVATE_KEY_HEADER,
} from "@/features/processor-config/certificate-schemas"
import { useReplaceMerchantMtlsCertificateMutation } from "@/services/admin/merchant-mtls-certificates"

import { MtlsCertificatePemFields } from "./mtls-certificate-pem-fields"

const panelPrefix = "pages.adminMerchants"

const buildSchema = (messages: {
  required: string
  invalidCertificateHeader: string
  invalidKeyHeader: string
}) =>
  z.object({
    certificatePem: z
      .string()
      .trim()
      .min(1, messages.required)
      .refine(
        (value) => PEM_CERTIFICATE_HEADER.test(value),
        messages.invalidCertificateHeader
      ),
    privateKeyPem: z
      .string()
      .trim()
      .min(1, messages.required)
      .refine(
        (value) => PEM_PRIVATE_KEY_HEADER.test(value),
        messages.invalidKeyHeader
      ),
  })

type ReplaceMtlsCertificateValues = z.infer<
  ReturnType<typeof buildSchema>
>

type ReplaceMtlsCertificateDialogProps = {
  certificateId: string
  certificateName: string
  linkedConfigsCount: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ReplaceMtlsCertificateDialog = ({
  certificateId,
  certificateName,
  linkedConfigsCount,
  open,
  onOpenChange,
}: ReplaceMtlsCertificateDialogProps) => {
  const { t } = useTranslation()
  const replaceMutation =
    useReplaceMerchantMtlsCertificateMutation(certificateId)

  const schema = useMemo(
    () =>
      buildSchema({
        required: t(
          "pages.adminProcessorConfig.form.required"
        ),
        invalidCertificateHeader: t(
          "pages.adminProcessorConfig.certificate.invalidCertificateHeader"
        ),
        invalidKeyHeader: t(
          "pages.adminProcessorConfig.certificate.invalidKeyHeader"
        ),
      }),
    [t]
  )

  const form = useForm<ReplaceMtlsCertificateValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      certificatePem: "",
      privateKeyPem: "",
    },
  })

  const handleSubmit = form.handleSubmit((values) => {
    replaceMutation.mutate(
      {
        certificatePem: encodePemToBase64(
          values.certificatePem
        ),
        privateKeyPem: encodePemToBase64(
          values.privateKeyPem
        ),
      },
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      }
    )
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) form.reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              `${panelPrefix}.mtlsCertificates.replaceTitle`,
              { name: certificateName }
            )}
          </DialogTitle>
          <DialogDescription>
            {/* O alcance vem antes do formulário: substituir aqui vale para
                todas as configurações vinculadas, não só para uma. */}
            {t(
              `${panelPrefix}.mtlsCertificates.replaceDescription`,
              { count: linkedConfigsCount }
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <MtlsCertificatePemFields form={form} />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={replaceMutation.isPending}
              >
                {t(
                  `${panelPrefix}.mtlsCertificates.replaceSubmit`
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
